import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { datasourceEndpoints as endpoints, documentsEndpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import {
  onDatasourcesDocumentsCreated,
  onDatasourcesDocumentsDeleted,
  onDatasourcesDocumentsUpdated,
} from '@/modules/collections/hooks/useAPIDatasourcesDocuments';
import { useTabCategory } from '@/modules/collections/hooks/useCategory';
import {
  Datasource,
  DatasourceStatus,
  DatasourceType,
  Datasources,
  PartialDatasource,
} from '@/shared/types/collections';
import { formatDate, getSecondsDifference } from '@/util/dateUtils';
import { durationFormat } from '@/util/util';

import useHomeCheckList, { AccountUserPrefsEnum } from './useHomeCheckList';
import { createEntityCache } from './utils/entityCache';
import { queryKeyFactories } from './utils/queryKeyFactory';

const TWO_SECONDS = 2_000;

const emptyCollectionFiles = {
  total_count: 0,
  pending_count: 0,
  indexing_count: 0,
  failed_count: 0,
  available_count: 0,
};

const emptyFileCategories = { files: 0, website: 0, articles: 0 };

export const API = Object.freeze({
  listDatasources: async (collectionId: string): Promise<Datasources> =>
    callGet(endpoints.allDatasources(collectionId)),

  getDatasource: async (collectionId: string, datasourceId: string): Promise<Datasource> =>
    callGet(endpoints.datasource(collectionId, datasourceId)),

  createDatasource: async (
    collectionId: string,
    datasource: Partial<Datasource>
  ): Promise<Datasource> => callPost(endpoints.datasources(collectionId), datasource),

  updateDatasource: async (
    collectionId: string,
    { datasource_id, ...datasource }: PartialDatasource
  ): Promise<Datasource> => callPut(endpoints.datasource(collectionId, datasource_id), datasource),

  syncDatasource: async (
    collectionId: string,
    { datasource_id, ...datasource }: PartialDatasource
  ): Promise<Datasource> =>
    callPut(endpoints.syncDatasource(collectionId, datasource_id), datasource),

  deleteDatasource: async (collectionId: string, datasourceId: string): Promise<Datasource> =>
    callDelete(endpoints.datasource(collectionId, datasourceId)),
});

export const datasourceCache = createEntityCache<Datasource, 'datasources'>({
  listKey: (collectionId) => [endpoints.datasources(collectionId)],
  detailKey: (collectionId, datasourceId) => [endpoints.datasource(collectionId, datasourceId)],
  listField: 'datasources',
  getId: (datasource) => datasource.datasource_id,
  getParentId: (datasource) => datasource.collection_id,
  afterChange: (queryClient, change) => {
    if (change.type === 'updated') {
      queryClient.invalidateQueries({
        queryKey: [documentsEndpoints.documents(change.parentId, change.entity.datasource_id)],
      });
    }
  },
});

const useDatasources = (collectionId: string, datasourceId?: string, updateTableView?: boolean) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { markAsComplete } = useHomeCheckList();

  const [refetchInterval, setRefetchInterval] = useState<number>();
  const { category, isWebsiteTab, isExternalTab } = useTabCategory();

  const {
    data: datasources,
    status: listStatus,
    isLoading: isListLoading,
  } = useQuery<Datasources, Error>({
    queryKey: [endpoints.datasources(collectionId)],
    queryFn: () => API.listDatasources(collectionId),
    enabled: !!collectionId,
  });

  const { data: datasource, status: getStatus } = useQuery<Datasource, Error>({
    queryKey: [endpoints.datasource(collectionId, datasourceId)],
    queryFn: () => API.getDatasource(collectionId, datasourceId),
    enabled: !!collectionId && !!datasourceId,
    refetchInterval,
  });

  useEffect(() => {
    if (
      datasource?.status === DatasourceStatus.PROCESSING ||
      datasource?.pending_count > 0 ||
      datasource?.indexing_count > 0
    ) {
      setRefetchInterval(TWO_SECONDS);
    } else {
      setRefetchInterval(undefined);
    }

    // Only invalidate queries when datasource actually changes or is processing
    if (datasource?.datasource_id) {
      queryClient.invalidateQueries({
        queryKey: [documentsEndpoints.documents(collectionId, datasource.datasource_id)],
      });

      // Only invalidate for webpages/external tabs when datasource is processing or has pending items
      if (
        (isWebsiteTab || isExternalTab) &&
        (datasource.status === DatasourceStatus.PROCESSING ||
          datasource.pending_count > 0 ||
          datasource.indexing_count > 0)
      ) {
        queryClient.invalidateQueries({
          queryKey: queryKeyFactories.folderDocuments(collectionId, datasource.datasource_id),
        });

        queryClient.invalidateQueries({
          queryKey: queryKeyFactories.datasourcesDocuments(collectionId, category),
        });
      }
    }
  }, [
    collectionId,
    datasource?.datasource_id,
    datasource?.status,
    datasource?.pending_count,
    datasource?.indexing_count,
    queryClient,
    category,
    isWebsiteTab,
    isExternalTab,
  ]);

  const {
    mutate: updateDatasource,
    mutateAsync: updateDatasourceAsync,
    status: updateStatus,
  } = useMutation<Datasource, Error, PartialDatasource>({
    mutationFn: (data) => API.updateDatasource(collectionId, data),
    onSuccess: (resp) => {
      datasourceCache.updated(queryClient, resp);
      if (updateTableView) {
        onDatasourcesDocumentsUpdated(queryClient, collectionId, category, resp.datasource_id);
      }
      toast.success(t('collections.datasources.updated', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const { mutate: syncDatasource } = useMutation<Datasource, Error, PartialDatasource>({
    mutationFn: (data) => API.syncDatasource(collectionId, data),
    onSuccess: (resp) => {
      datasourceCache.updated(queryClient, resp);
      toast.success(t('collections.datasources.synced', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const {
    mutate: createDatasource,
    mutateAsync: createDatasourceAsync,
    status: createStatus,
  } = useMutation<Datasource, Error, Partial<Datasource>>({
    mutationFn: (data) => API.createDatasource(collectionId, data),
    onSuccess: (resp) => {
      markAsComplete(AccountUserPrefsEnum.IMPORT_CONTENT);
      datasourceCache.created(queryClient, resp);
      onDatasourcesDocumentsCreated(queryClient, collectionId, category, resp.datasource_id, null);
      toast.success(t('collections.datasources.created', { 0: resp?.name }));
    },
    meta: { errorToast: true },
  });

  const {
    mutate: deleteDatasource,
    mutateAsync: deleteDatasourceAsync,
    status: deleteStatus,
  } = useMutation<Datasource, Error, string>({
    mutationFn: (id) => API.deleteDatasource(collectionId, id ?? datasourceId),
    onSuccess: (resp) => {
      datasourceCache.removed(queryClient, { parentId: collectionId, id: resp.datasource_id });
      onDatasourcesDocumentsDeleted(
        queryClient,
        collectionId,
        category,
        resp.datasource_id,
        undefined
      );
      toast.success(t('collections.datasources.deleted'));
    },
    meta: { errorToast: true },
  });

  const listDatasources = useCallback(
    async (collection_id: string): Promise<Datasources> => {
      const cachedDatasource = queryClient.getQueryData<Datasources>([
        endpoints.datasources(collection_id),
      ]);

      if (cachedDatasource) {
        return cachedDatasource;
      }
      const foundDatasources = await API.listDatasources(collection_id);

      queryClient.setQueryData<Datasources>([endpoints.datasources(collection_id)], (prev) => ({
        ...prev,
        ...foundDatasources,
      }));

      return foundDatasources;
    },
    [queryClient]
  );

  const collectionFiles = useMemo(
    () =>
      datasources?.datasources.reduce(
        (acc, d) => {
          acc.total_count += d.document_count;
          acc.pending_count += d.pending_count;
          acc.indexing_count += d.indexing_count;
          acc.failed_count += d.failed_count;
          acc.available_count +=
            d.document_count - d.pending_count - d.indexing_count - d.failed_count;

          return acc;
        },
        { ...emptyCollectionFiles }
      ) ?? emptyCollectionFiles,
    [datasources?.datasources]
  );

  const fileCategories = useMemo(
    () =>
      datasources?.datasources.reduce(
        (acc, d) => {
          if (d.type === DatasourceType.INTERCOM_KB || d.type === DatasourceType.ZENDESK_KB) {
            acc.articles += d.document_count - d.pending_count - d.indexing_count - d.failed_count;
          } else {
            acc[d.type] += d.document_count - d.pending_count - d.indexing_count - d.failed_count;
          }
          return acc;
        },
        { ...emptyFileCategories }
      ) ?? emptyFileCategories,
    [datasources?.datasources]
  );

  const datasourceNames = useMemo(
    () => datasources?.datasources?.map((d) => d.name.toLowerCase()),
    [datasources?.datasources]
  );

  function generateModalReportData() {
    const start_time = datasource?.last_indexed_details?.start_time || null;
    const end_time = datasource?.last_indexed_details?.end_time || null;
    const warnings = datasource?.last_indexed_details?.warnings || [];
    const errors = datasource?.last_indexed_details?.errors || [];
    const document_count = datasource?.document_count || 0;

    const formatMoment = (date) => formatDate(date, 'dd/MM/yy HH:mm:ss');
    const formatDuration = (start, end) => {
      const diff = getSecondsDifference(end, start);
      return durationFormat(t, diff / 60);
    };

    return [
      { title: t('collections.start'), value: start_time ? formatMoment(start_time) : '-' },
      { title: t('collections.end'), value: end_time ? formatMoment(end_time) : '-' },
      {
        title: t('collections.total_time'),
        value: start_time ? formatDuration(start_time, end_time) : '-',
      },
      { title: t('collections.total_urls'), value: `${document_count + warnings.length}` },
      { title: t('collections.urls_ingested'), value: `${document_count}` },
      { title: t('collections.urls_not_ingested'), value: `${warnings.length}`, urls: warnings },
      { title: t('collections.urls_excluded'), value: `${errors.length}`, urls: errors },
    ];
  }

  return {
    datasources: datasources?.datasources,
    isListLoading,
    collectionFiles,
    fileCategories,
    datasource,
    getStatus,
    listStatus,
    createStatus,
    deleteStatus,
    updateStatus,
    createDatasource,
    createDatasourceAsync,
    deleteDatasource,
    deleteDatasourceAsync,
    updateDatasource,
    updateDatasourceAsync,
    syncDatasource,
    listDatasources,
    datasourceNames,
    generateModalReportData,
  };
};

export default useDatasources;
